"use client";

import Link from "next/link";
import { cn } from "#/lib/utils";
import { Button } from "#/components/ui/button";
import dynamic from "next/dynamic";
import { Menu } from "lucide-react";
import { navigationItems } from "#/data/navigation";
import { useEffect, useRef, useState } from "react";
import { useMotionValueEvent, useScroll } from "motion/react";
import { ThemeToggle } from "#/components/theme-toggle";
import { useSections } from "#/contexts/sections-context";
import { handleSectionClick } from "#/lib/utils/scroll";

const Sheet = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.Sheet), { ssr: false });
const SheetContent = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.SheetContent), { ssr: false });
const SheetTrigger = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.SheetTrigger), { ssr: false });
const SheetTitle = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.SheetTitle), { ssr: false });
const SheetHeader = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.SheetHeader), { ssr: false });
const SheetDescription = dynamic(() => import("#/components/ui/sheet").then((mod) => mod.SheetDescription), {
  ssr: false,
});

export function Navigation() {
  const [activeSection, setActiveSection] = useState("");
  const [showBanner, setShowBanner] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLAnchorElement>(null);
  const { scrollY } = useScroll();
  const [blur, setBlur] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollSource = useRef<"manual" | "menu">("manual");
  const menuScrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const sectionsRef = useRef<Array<{ id: string; top: number; height: number }>>([]);
  const { preloadSectionsUntil } = useSections();

  const detectActiveSection = (scrollPosition: number) => {
    const updatedSections = navigationItems.map((item) => {
      const id = item.href.substring(1);
      const element = document.getElementById(id);
      return {
        id,
        top: element?.offsetTop || 0,
        height: element?.offsetHeight || 0,
      };
    });

    sectionsRef.current = updatedSections;

    const currentSection = updatedSections.find((section) => {
      const sectionTop = section.top;
      const sectionBottom = section.top + section.height;
      return scrollPosition >= sectionTop && scrollPosition < sectionBottom;
    });

    if (currentSection && currentSection.id !== activeSection) {
      setActiveSection(currentSection.id);
    } else if (!currentSection && updatedSections.length > 0 && scrollPosition < 100) {
      setActiveSection(updatedSections[0].id);
    }
  };

  const handleScroll = (latest: number) => {
    setBlur(latest > 30);

    if (scrollSource.current === "menu") {
      lastScrollY.current = latest;
      return;
    }

    const direction = latest > lastScrollY.current ? "down" : "up";
    if (direction === "down" && latest > 150) {
      setHidden(true);
    } else if (direction === "up") {
      setHidden(false);
    }

    detectActiveSection(latest + 500);
    lastScrollY.current = latest;
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    handleSectionClick(e, href, preloadSectionsUntil, {
      scrollSource,
      menuScrollTimeout,
      setActiveSection,
      offset: 60,
      delay: 100,
      scrollDuration: 1000,
    });
  };

  const handleSheetNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    handleNavClick(e, href);
    const closestButton = document.querySelector('[data-state="open"]') as HTMLButtonElement;
    if (closestButton) closestButton.click();
  };

  const updateActiveTabHighlight = () => {
    const container = containerRef.current;
    if (container && activeSection) {
      const activeTabElement = activeTabRef.current;
      if (activeTabElement) {
        const { offsetLeft, offsetWidth } = activeTabElement;
        const clipLeft = offsetLeft;
        const clipRight = offsetLeft + offsetWidth;
        container.style.clipPath = `inset(0 ${Number(100 - (clipRight / container.offsetWidth) * 100).toFixed()}% 0 ${Number((clipLeft / container.offsetWidth) * 100).toFixed()}% round 8px)`;
      }
    }
  };

  const renderNavItem = (item: (typeof navigationItems)[0], isInSheet = false) => {
    const isActive = activeSection === item.href.substring(1);
    return (
      <a
        key={item.href}
        href={item.href}
        ref={isActive && !isInSheet ? activeTabRef : null}
        onClick={(e) => (isInSheet ? handleSheetNavClick(e, item.href) : handleNavClick(e, item.href))}
        className={
          isInSheet
            ? cn(
                "group flex items-center space-x-3 rounded-md px-3 py-2 text-sm font-medium transition-all",
                "hover:bg-accent hover:text-accent-foreground focus-visible:ring-primary/50 focus-visible:ring-2 focus-visible:outline-none",
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
              )
            : "flex h-8 items-center rounded-full p-3 text-sm font-medium text-neutral-500 dark:text-neutral-300"
        }
      >
        <span>{item.label}</span>
      </a>
    );
  };

  useEffect(() => {
    sectionsRef.current = navigationItems.map((item) => {
      const id = item.href.substring(1);
      const element = document.getElementById(id);
      return {
        id,
        top: element?.offsetTop || 0,
        height: element?.offsetHeight || 0,
      };
    });

    detectActiveSection(window.scrollY + 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMotionValueEvent(scrollY, "change", handleScroll);

  useEffect(() => {
    updateActiveTabHighlight();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  return (
    <>
      {showBanner && (
        <div className="fixed top-0 z-[101] flex w-full items-center justify-center bg-gradient-to-r from-emerald-500 to-teal-500 py-1.5 text-center text-sm font-medium text-white shadow-md">
          <div className="flex max-w-[90%] items-center">
            <span className="mr-2">🚧</span>
            <span>
              Portfolio en cours de construction. Des améliorations sont en cours. La version finale sera communiquée
              depuis mon LinkedIn.
            </span>
            <span className="ml-2">🔄</span>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="absolute top-1/2 right-3 -translate-y-1/2 transform text-white transition-colors hover:text-gray-200"
            aria-label="Fermer la bannière"
          >
            ✕
          </button>
        </div>
      )}
      <header
        className={cn(
          "fixed z-[40] w-full transition-all duration-200",
          blur ? "shadow-md backdrop-blur-md" : "bg-transparent",
          showBanner ? "top-[36px]" : "top-0",
          hidden ? "-translate-y-full" : "translate-y-0"
        )}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between px-4 sm:px-6">
          <Link href="/" className="flex items-center text-xl font-bold tracking-tight">
            <span className="text-primary font-extrabold">Jeff</span>
            <span className="ml-1.5 font-medium">Lefloch</span>
          </Link>
          <div className="flex gap-2">
            <nav className="relative hidden items-center gap-8 md:flex">
              <div
                ref={containerRef}
                className="absolute z-10 w-full overflow-hidden [clip-path:inset(0px_100%_0px_0%_round_8px)] [transition:clip-path_0.25s_ease]"
              >
                <div className="relative flex w-full justify-center bg-black dark:bg-white">
                  {navigationItems.map((item) => (
                    <a
                      key={`bg-${item.href}`}
                      href={item.href}
                      className="flex h-8 items-center rounded-full p-3 text-sm font-medium text-white dark:text-black"
                      tabIndex={-1}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="relative flex w-full justify-center">
                {navigationItems.map((item) => renderNavItem(item))}
              </div>
            </nav>

            <div className="hidden md:flex">
              <ThemeToggle />
            </div>
          </div>

          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="border-border/40 bg-background/95 border-l backdrop-blur-md">
              <SheetHeader className="border-border/10 border-b pb-4">
                <SheetTitle className="text-primary text-lg font-bold">Menu de navigation</SheetTitle>
                <SheetDescription className="text-muted-foreground">
                  Naviguez vers les différentes sections de mon portfolio.
                </SheetDescription>
              </SheetHeader>
              <nav className="m-6 flex flex-col space-y-5 py-4">
                {navigationItems.map((item) => renderNavItem(item, true))}
                <div className="border-border/10 mt-6 border-t pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">Thème</span>
                    <ThemeToggle />
                  </div>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
}
